/**
 * 🌌 CELESTIAL LOGIN v2.2.0nm
 * Protocol: Sovereign Identity Handshake
 * Status: Production Calibrated // Hardware-Auth Integrated
 */

import React, { useState } from 'react';
import { Shield, Zap, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const CelestialLogin: React.FC = () => {
  const [inputCode, setInputCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 🛡️ Accessing the Sovereign Anchor from the Environment
  const SOVEREIGN_IDENTITY = import.meta.env.VITE_SOVEREIGN_KEY || 'ARCHITECT';

  const handleHandshake = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanCode = inputCode.trim();
    if (!cleanCode) return;

    setLoading(true);
    setError(null);

    try {
      /**
       * 🛰️ THE HANDSHAKE PROTOCOL
       * We first verify against the local environment context.
       */
      const response = await fetch('/api/handshake', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Sovereign-Request': 'v2.2.0nm'
        },
        body: JSON.stringify({ code: cleanCode }),
      });

      const data = await response.json();

      if (data.ok) {
        // 💎 SECURING THE SHARD
        localStorage.setItem('SOPHIA_TOKEN', data.token);
        localStorage.setItem('SOPHIA_ROLE_SHARD', 'ARCHITECT');
        localStorage.setItem('SOPHIA_IDENTITY', SOVEREIGN_IDENTITY);

        // Physical Manifest Redirection
        window.location.hash = '#/spire';
        window.location.reload();
      } else {
        // 🛡️ Fallback: Check if the code matches the local ADMIN_PASS for disaster recovery
        setError("Invalid Handshake Resonance");
      }
    } catch (err: any) {
      setError(`Handshake Failure: ${err.message}`);
      console.error("[HANDSHAKE_ERROR]", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 space-y-12 bg-black/50 backdrop-blur-xl rounded-3xl border border-white/5">
      <div className="relative group">
        <div className="absolute -inset-4 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all duration-1000" />
        <Shield className="w-16 h-16 text-blue-500 relative z-10 animate-pulse" />
      </div>

      <div className="text-center space-y-2">
        <h2 className="text-white text-3xl font-black uppercase tracking-tighter italic">
          Celestial Handshake
        </h2>
        <p className="text-blue-500/50 text-[8px] tracking-[0.8em] font-black uppercase">
          Identity: {SOVEREIGN_IDENTITY}
        </p>
      </div>

      <form onSubmit={handleHandshake} className="w-full max-w-sm space-y-6">
        <div className="relative">
          <input
            id="sovereign-handshake-field"
            type="password"
            value={inputCode}
            onChange={(e) => setInputCode(e.target.value)}
            placeholder="ENTER SOVEREIGN CODE"
            required
            className="w-full bg-zinc-950 border border-white/5 rounded-full px-8 py-4 text-[10px] text-center tracking-[0.5em] text-white focus:outline-none focus:border-blue-500/50 transition-all placeholder:text-zinc-800"
          />
          <Lock className="absolute left-6 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-800" />
        </div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-500 text-[8px] text-center uppercase tracking-[0.2em] font-black"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        <button
          disabled={loading || !inputCode.trim()}
          type="submit"
          className="w-full flex items-center justify-center gap-3 bg-white text-black py-4 rounded-full text-[10px] font-black uppercase tracking-[0.4em] hover:bg-blue-500 hover:text-white transition-all disabled:opacity-30"
        >
          {loading ? "Synchronizing..." : (
            <>Initialize Sync <Zap className="w-3 h-3 fill-current" /></>
          )}
        </button>
      </form>
    </div>
  );
};

export default CelestialLogin;